// Construye la estructura de datos del cronograma de pagos (calendario) a
// partir de las cuotas y los abonos de un crédito Diario o Semanal.
//
// Reglas de negocio (ver especificación funcional):
// - El rango de fechas va desde la fecha_vencimiento de la primera cuota
//   hasta la de la última cuota, extendido a semanas completas (Lunes a
//   Domingo) para poder pintar el grid de 7 columnas.
// - El estado de cada día con cuota sale de cruzar fecha_vencimiento con
//   fecha_pago de los abonos (NUNCA con el campo "estado" de la cuota, ni
//   con la fecha de "hoy").
// - Un día sin cuota se pinta como "domingo" (si cae domingo) o "neutra"
//   (cualquier otro día de la semana). Si además de no tener cuota cae
//   domingo, domingo gana. Si SÍ tiene cuota, la cuota manda siempre,
//   incluso si cae domingo.
//
// El parseo de fechas es tolerante a distintos formatos que puede mandar el
// backend ("2026-08-17", "2026-08-17 00:00:00", "2026-08-17T00:00:00.000Z",
// "17/08/2026") para no depender de un único formato exacto.

const MS_DAY = 24 * 60 * 60 * 1000;

const MESES_LARGO = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const MESES_CORTO = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

const ISO_DATE_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})/;
const SLASH_DATE_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/; // dd/mm/yyyy

// Convierte un valor de fecha (string en varios formatos posibles, o Date)
// en un timestamp UTC a medianoche. Devuelve null si no se pudo interpretar
// (nunca NaN ni un timestamp "adivinado"), para poder detectar y descartar
// fechas inválidas explícitamente en vez de arrastrar basura (p.ej. caer en
// 1970-01-01 por culpa de `new Date(null)` / `new Date(undefined)`).
const parseDateOnly = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return null;
  }

  if (rawValue instanceof Date) {
    if (isNaN(rawValue.getTime())) return null;
    return Date.UTC(
      rawValue.getUTCFullYear(),
      rawValue.getUTCMonth(),
      rawValue.getUTCDate()
    );
  }

  if (typeof rawValue !== "string") return null;
  const value = rawValue.trim();

  let match = value.match(ISO_DATE_RE);
  if (match) {
    const y = Number(match[1]);
    const mo = Number(match[2]);
    const d = Number(match[3]);
    if (y && mo && d) return Date.UTC(y, mo - 1, d);
    return null;
  }

  match = value.match(SLASH_DATE_RE);
  if (match) {
    const d = Number(match[1]);
    const mo = Number(match[2]);
    const y = Number(match[3]);
    if (y && mo && d) return Date.UTC(y, mo - 1, d);
    return null;
  }

  return null;
};

const formatDateOnly = (ts) => {
  const d = new Date(ts);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const monthYearLabel = (ts) => {
  const d = new Date(ts);
  return `${MESES_LARGO[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

const shortDateLabel = (ts) => {
  const d = new Date(ts);
  return `${d.getUTCDate()} ${MESES_CORTO[d.getUTCMonth()]}`;
};

// Índice de columna con la semana empezando en Lunes (Lunes=0 ... Domingo=6)
const colIndexMonFirst = (ts) => {
  const dow = new Date(ts).getUTCDay(); // 0=Domingo
  return (dow + 6) % 7;
};

// Los nombres de campo son los documentados por el backend, pero se acepta
// una variante en camelCase por si acaso, sin adivinar nada más.
const getFechaVencimiento = (cuota) =>
  cuota.fecha_vencimiento ?? cuota.fechaVencimiento ?? null;

const getFechaPago = (abono) => abono.fecha_pago ?? abono.fechaPago ?? null;

/**
 * @param {Object} params
 * @param {Array} params.cuotas - dataModalDetalleCuotas.cuotas
 * @param {Array} params.abonos - dataModalDetalleAbonos.abonos
 * @param {String} params.fechaUltimoPagoProgramado - dataModalDetalle.fecha_ultimo_pago_programado
 * @returns {{ resumen: Object|null, semanas: Array, estadoVacio: String|null }}
 */
export const buildCronograma = ({ cuotas, abonos, fechaUltimoPagoProgramado }) => {
  const cuotasArray = Array.isArray(cuotas) ? cuotas : [];

  if (cuotasArray.length === 0) {
    return { resumen: null, semanas: [], estadoVacio: "sin_cuotas" };
  }

  // Solo se descartan las cuotas cuya fecha no se puede interpretar en
  // ningún formato conocido; el resto sigue el flujo normal.
  const cuotasConFecha = [];
  let cuotasFechaInvalida = 0;
  cuotasArray.forEach((c) => {
    if (!c) return;
    const ts = parseDateOnly(getFechaVencimiento(c));
    if (ts === null) {
      cuotasFechaInvalida += 1;
      return;
    }
    cuotasConFecha.push({ cuota: c, ts, fechaStr: formatDateOnly(ts) });
  });

  if (cuotasConFecha.length === 0) {
    if (process.env.NODE_ENV !== "production") {
      // Ayuda a diagnosticar rápido desde la consola del navegador si el
      // backend cambia el nombre o el formato del campo de fecha.
      // eslint-disable-next-line no-console
      console.warn(
        "[Cronograma] No se pudo leer fecha_vencimiento en ninguna cuota. " +
          "Primer registro recibido:",
        cuotasArray[0]
      );
    }
    return { resumen: null, semanas: [], estadoVacio: "fechas_invalidas" };
  }

  cuotasConFecha.sort((a, b) => {
    if (a.ts !== b.ts) return a.ts - b.ts;
    return (a.cuota.numero || 0) - (b.cuota.numero || 0);
  });

  const cuotasPorFecha = new Map();
  cuotasConFecha.forEach(({ cuota, fechaStr }) => {
    cuotasPorFecha.set(fechaStr, cuota);
  });

  // Filtra abonos posteriores a la fecha fin programada del crédito: pueden
  // llegar del backend pero no deben pintarse en el cronograma. La
  // comparación se hace por timestamp (no por string) para no depender del
  // formato exacto de cada fecha.
  const tsUltimoPagoProgramado = parseDateOnly(fechaUltimoPagoProgramado);

  const abonosValidos = Array.isArray(abonos) ? abonos : [];
  const abonosConFecha = [];
  abonosValidos.forEach((a) => {
    if (!a) return;
    const ts = parseDateOnly(getFechaPago(a));
    if (ts === null) return;
    if (tsUltimoPagoProgramado !== null && ts > tsUltimoPagoProgramado) return;
    abonosConFecha.push({ abono: a, ts, fechaStr: formatDateOnly(ts) });
  });

  // Agrupa por fecha_pago (por si hubiera más de un abono el mismo día) y
  // se queda con el saldo_pendiente del abono más reciente de ese día.
  const abonosPorFecha = new Map();
  abonosConFecha.forEach(({ abono: a, fechaStr }) => {
    const montoTotal = Number(a.monto_total) || 0;
    const marcaTiempo = a.updated_at || a.created_at || "";
    const existente = abonosPorFecha.get(fechaStr);
    if (!existente) {
      abonosPorFecha.set(fechaStr, {
        montoTotal,
        saldoPendiente: a.saldo_pendiente,
        _marcaTiempo: marcaTiempo,
      });
    } else {
      existente.montoTotal += montoTotal;
      if (marcaTiempo >= existente._marcaTiempo) {
        existente.saldoPendiente = a.saldo_pendiente;
        existente._marcaTiempo = marcaTiempo;
      }
    }
  });

  const primeraCuota = cuotasConFecha[0];
  const ultimaCuota = cuotasConFecha[cuotasConFecha.length - 1];
  const tsInicio = primeraCuota.ts;
  const tsFin = ultimaCuota.ts;

  // Extiende a semanas completas (Lunes a Domingo) para el grid.
  const tsInicioGrid = tsInicio - colIndexMonFirst(tsInicio) * MS_DAY;
  const tsFinGrid = tsFin + (6 - colIndexMonFirst(tsFin)) * MS_DAY;

  // "Hoy" es solo una marca visual (como inicio/fin), nunca se usa para
  // decidir si una cuota está pendiente o vencida (eso sigue saliendo
  // exclusivamente de cruzar fecha_vencimiento con fecha_pago). Se calcula
  // con la fecha local del navegador (no UTC) para que coincida con el
  // calendario real del usuario. Solo se resalta si "hoy" cae dentro del
  // rango real del crédito (primera a última cuota); si el crédito ya
  // terminó (hoy > fecha fin) o todavía no empieza, no se resalta nada.
  const ahora = new Date();
  const tsHoy = Date.UTC(
    ahora.getFullYear(),
    ahora.getMonth(),
    ahora.getDate()
  );
  const hoyEnRangoDelCredito = tsHoy >= tsInicio && tsHoy <= tsFin;

  const resumen = {
    inicioLabel: shortDateLabel(tsInicio),
    finLabel: shortDateLabel(tsFin),
    totalCuotas: cuotasConFecha.length,
    pagadas: 0,
    parcial: 0,
    sinAbono: 0,
    pendiente: 0,
  };

  const dias = [];
  for (let ts = tsInicioGrid; ts <= tsFinGrid; ts += MS_DAY) {
    const fechaStr = formatDateOnly(ts);
    const cuota = cuotasPorFecha.get(fechaStr);
    const abono = abonosPorFecha.get(fechaStr);
    const colIndex = colIndexMonFirst(ts);
    const dayOfMonth = new Date(ts).getUTCDate();
    const esDomingo = colIndex === 6;

    if (cuota) {
      const valorCuota = Number(cuota.valor_cuota) || 0;
      let estado;
      if (!abono) {
        estado = "pendiente";
      } else if (abono.montoTotal === 0) {
        estado = "sin_abono";
      } else if (abono.montoTotal < valorCuota) {
        estado = "parcial";
      } else {
        estado = "pagada";
      }

      if (estado === "pagada") resumen.pagadas += 1;
      else if (estado === "parcial") resumen.parcial += 1;
      else if (estado === "sin_abono") resumen.sinAbono += 1;
      else resumen.pendiente += 1;

      dias.push({
        key: fechaStr,
        fechaStr,
        dayOfMonth,
        colIndex,
        tieneCuota: true,
        estado,
        numero: cuota.numero,
        valorCuota,
        montoAbonado: abono ? abono.montoTotal : null,
        saldoRestante: abono ? abono.saldoPendiente : null,
        esInicio: fechaStr === primeraCuota.fechaStr,
        esFin: fechaStr === ultimaCuota.fechaStr,
        esHoy: hoyEnRangoDelCredito && ts === tsHoy,
      });
    } else {
      dias.push({
        key: fechaStr,
        fechaStr,
        dayOfMonth,
        colIndex,
        tieneCuota: false,
        estado: esDomingo ? "domingo" : "neutra",
        esDomingo,
        esHoy: hoyEnRangoDelCredito && ts === tsHoy,
      });
    }
  }

  const semanas = [];
  for (let i = 0; i < dias.length; i += 7) {
    semanas.push(dias.slice(i, i + 7));
  }

  let ultimoHeader = null;
  const semanasConHeader = semanas.map((semanaDias, idx) => {
    const diaUno = semanaDias.find((d) => d.dayOfMonth === 1);
    let headerLabel = null;
    if (diaUno) {
      headerLabel = monthYearLabel(parseDateOnly(diaUno.fechaStr));
    } else if (idx === 0) {
      headerLabel = monthYearLabel(parseDateOnly(semanaDias[0].fechaStr));
    }
    if (headerLabel && headerLabel === ultimoHeader) {
      headerLabel = null;
    } else if (headerLabel) {
      ultimoHeader = headerLabel;
    }
    return { key: `semana-${idx}`, headerLabel, dias: semanaDias };
  });

  if (process.env.NODE_ENV !== "production" && cuotasFechaInvalida > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Cronograma] ${cuotasFechaInvalida} cuota(s) se omitieron del calendario por tener fecha_vencimiento inválida o en un formato no reconocido.`
    );
  }

  return { resumen, semanas: semanasConHeader, estadoVacio: null };
};

export default buildCronograma;
